import { Node, type NodeViewProps } from "@tiptap/core";
import { NodeViewWrapper, ReactNodeViewRenderer } from "@tiptap/react";
import { useMeasure } from "@uidotdev/usehooks";
import { useEffect, useState } from "react";

export interface IframeOptions {
	allowFullscreen: boolean;
	HTMLAttributes: {
		[key: string]: any;
	};
}

declare module "@tiptap/core" {
	interface Commands<ReturnType> {
		iframe: {
			/**
			 * Add an iframe
			 */
			setIframe: (options: { src: string; video?: boolean }) => ReturnType;
		};
	}
}

function IframeRenderer({
	video,
	src,
	width: windowWidth,
	height: windowHeight,
}: {
	video?: boolean;
	src: string;
	width?: number;
	height?: number;
}) {
	const [parentRef, { width }] = useMeasure();
	const [canRender, setCanRender] = useState(false);

	useEffect(() => {
		setTimeout(() => {
			setCanRender(true);
		}, 0);
	}, []);

	return (
		<div
			ref={parentRef}
			className="flex h-[600px] w-full max-w-full items-center overflow-clip"
			style={{
				...(video ? { aspectRatio: 16 / 9 } : {}),
			}}
		>
			{canRender ? (
				<iframe
					src={src}
					title={video ? "video" : "external-site"}
					className="h-full w-full rounded-md"
					style={{
						...(windowWidth
							? { maxWidth: Math.min(windowWidth, width || 0) }
							: {}),
						...(windowHeight ? { maxHeight: windowHeight } : {}),
						...(video ? { aspectRatio: 16 / 9 } : {}),
					}}
				/>
			) : null}
		</div>
	);
}

function IframeComponent(props: NodeViewProps) {
	return (
		<NodeViewWrapper className="react-component">
			<IframeRenderer
				width={props.node.attrs.width}
				height={props.node.attrs.height}
				src={props.node.attrs.src}
				video={props.node.attrs.video}
			/>
		</NodeViewWrapper>
	);
}

export const iFrameExtension = Node.create<IframeOptions>({
	name: "iframe",

	group: "block",

	atom: true,

	addOptions() {
		return {
			allowFullscreen: false,
			HTMLAttributes: {
				class: "iframe-wrapper",
			},
		};
	},

	addAttributes() {
		return {
			src: {
				default: null,
			},
			video: {
				default: false,
			},
			frameborder: {
				default: 0,
			},
			allowfullscreen: {
				default: this.options.allowFullscreen,
				parseHTML: () => this.options.allowFullscreen,
			},
		};
	},

	parseHTML() {
		return [
			{
				tag: "iframe",
			},
		];
	},

	renderHTML({ HTMLAttributes, node }) {
		return [
			"div",
			{
				class: node.attrs.video ? "video-frame" : "iframe-wrapper",
			},
			[
				"iframe",
				{
					...HTMLAttributes,
					style: "width: 100%; height: 100%;",
				},
			],
		];
	},

	addNodeView() {
		return ReactNodeViewRenderer(IframeComponent);
	},

	addCommands() {
		return {
			setIframe:
				(options: { src: string }) =>
				({ commands }) => {
					return commands.insertContent([
						{
							type: this.name,
							attrs: options,
						},
						{
							type: "hardBreak",
						},
					]);
				},
		};
	},
});
